import { CircleAlert } from 'lucide-react';
import { debounce } from 'lodash';

import { ToggleGroupCard, ToggleGroupCardItem } from '@/components/ui/toggle-group';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AgreementConditionEnum, type IAgreementUpdateParams } from 'src/graphql/schema';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { releaseEventsOptionsRich as options } from '../model';

type Props = {
  title: string;
  description: string;
  onUpdate: (agreementParams: IAgreementUpdateParams) => void;
  conditions?: AgreementConditionEnum[];
  conditionsOther?: string;
  onOtherChange: (value: string) => void;
};

const AgreementConditionsSettings = ({
  title,
  description,
  onUpdate,
  conditions = [],
  conditionsOther,
  onOtherChange,
}: Props) => {
  const onConditionsChange = (conditions: AgreementConditionEnum[]) => onUpdate({ conditions });

  const onConditionsOtherChange = debounce(
    (event: React.ChangeEvent<HTMLInputElement>) => onOtherChange(event.target.value),
    500,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {title}
          {!conditions.length && (
            <span className="text-orange-700 flex items-center gap-1">
              <CircleAlert className="size-3.5" />
              <i className="text-xs font-medium">Required</i>
            </span>
          )}
        </CardTitle>
        <CardDescription className="whitespace-pre-line">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <ToggleGroupCard
          type="multiple"
          className="flex-col w-full"
          onValueChange={onConditionsChange}
          value={conditions}
        >
          {options.map(({ title, value, description, icon }) => (
            <ToggleGroupCardItem key={value} value={value} icon={icon} title={title} description={description} />
          ))}
        </ToggleGroupCard>

        {conditions.includes(AgreementConditionEnum.Other) && (
          <div className="mt-6">
            <Label htmlFor="conditionsOther">List your custom events:</Label>
            <Input
              id="conditionsOther"
              placeholder="Enter events"
              onChange={onConditionsOtherChange}
              defaultValue={conditionsOther || ''}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AgreementConditionsSettings;
